import React, { useState, useEffect, useMemo } from 'react';
import { Button, GhostSpinner } from 'components/ui';
import { getHostelRatesForStudent } from 'services/applicantService';

export const HostelFeeMatrix = ({ onNext, onBack, isInternational = false, academicAmount = 0, feeBreakup, setFeeBreakup }) => {
  const [selectedCell, setSelectedCell] = useState(feeBreakup?.hostel?.selectedCell || null); // { hostelName, roomType, messType, amount }
  const [hostelRates, setHostelRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const hostelAmount = selectedCell ? selectedCell.amount : 0;
  const grandTotal = academicAmount + hostelAmount;

  useEffect(() => {
    const fetchRates = async () => {
      setIsLoading(true);
      try {
        const res = await getHostelRatesForStudent(isInternational);
        if (res.success) {
          setHostelRates(res.data || []);
        }
      } catch (err) {
        console.error('Error fetching hostel rates:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRates();
  }, [isInternational]);

  // Group flat API data into a matrix structure
  const matrixData = useMemo(() => {
    const grouped = {};
    
    hostelRates.forEach(rate => {
      const name = rate.HostelName;
      if (!grouped[name]) {
        grouped[name] = {
          name: name,
          Single: { with: null, without: null, rateId: null },
          Double: { with: null, without: null, rateId: null },
          Triple: { with: null, without: null, rateId: null }
        };
      }
      
      const type = rate.RoomType; // "Single", "Double", "Triple"
      if (grouped[name][type]) {
        grouped[name][type].with = rate.WithMessAmount;
        grouped[name][type].without = rate.WithoutMessAmount;
        grouped[name][type].rateId = rate.HostelRatesId || rate.Id;
      }
    });
    
    return Object.values(grouped);
  }, [hostelRates]);

  const handleCellSelect = (hostelName, roomType, messType, amount, rateId) => {
    if (amount === null) return;
    
    // Toggle off if same selected
    if (selectedCell?.hostelName === hostelName && selectedCell?.roomType === roomType && selectedCell?.messType === messType) {
      setSelectedCell(null);
    } else {
      setSelectedCell({ hostelName, roomType, messType, amount, rateId });
    }
  };
  
  useEffect(() => {
    setFeeBreakup(prev => {
      // Stability check: if nothing meaningful changed, return prev
      if (
        prev.hostel.selectedCell === selectedCell ||
        (prev.hostel.selectedCell?.hostelName === selectedCell?.hostelName &&
         prev.hostel.selectedCell?.roomType === selectedCell?.roomType &&
         prev.hostel.selectedCell?.messType === selectedCell?.messType &&
         prev.hostel.selectedCell?.amount === selectedCell?.amount)
      ) {
        return prev;
      }

      return { 
        ...prev, 
        hostel: {
          ...prev.hostel,
          selectedCell,
          hostelName: selectedCell?.hostelName,
          roomType: selectedCell?.roomType,
          messType: selectedCell?.messType,
          amount: selectedCell?.amount || 0
        } 
      };
    });
  }, [selectedCell]);

  const handleNext = () => {
    const amount = selectedCell ? selectedCell.amount : 0;
    
    const details = selectedCell ? {
      hostelType: selectedCell.hostelName,
      roomType: selectedCell.roomType,
      mess: selectedCell.messType,
      period: 'quarterly',
      baseAmount: amount,
      totalHostelFee: amount,
      hostelRatesId: selectedCell.rateId
    } : null;

    setFeeBreakup(prev => ({ 
      ...prev, 
      hostel: {
        selectedCell,
        hostelName: details?.hostelType,
        roomType: details?.roomType,
        messType: details?.mess,
        amount: amount
      } 
    }));

    onNext(amount, details);
  };

  const currencySymbol = isInternational ? '$' : '₹';
  const currencyLabel = isInternational ? 'USD' : 'INR';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <GhostSpinner className="size-8 text-primary-600" />
        <p className="text-sm text-gray-500">Fetching hostel rates...</p>
      </div>
    );
  }

  const isAnySelected = !!selectedCell;

  return (
    <div className="space-y-5 animate-fade-in pb-4">
      <div className="bg-gray-50 dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-700">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-100">Hostel Fee Details</h3>
            <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">Select an accommodation type. Check the box to apply.</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-md border border-blue-100 dark:border-blue-800">
            <span className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
              Note: Quarterly Basis
            </span>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-700 shadow-sm">
        <table className="w-full text-xs text-center border-collapse">
          <thead>
            <tr className="bg-red-700 text-white">
              <th rowSpan="2" className="border border-red-800 p-2 text-left font-bold w-1/4">ACCOMMODATION</th>
              <th colSpan="2" className="border border-red-800 p-2 font-bold uppercase">Single Room</th>
              <th colSpan="2" className="border border-red-800 p-2 font-bold uppercase">Double Room</th>
              <th colSpan="2" className="border border-red-800 p-2 font-bold uppercase">Triple Room</th>
            </tr>
            <tr className="bg-red-700 text-white leading-tight">
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">With Mess<br/>{currencySymbol}</th>
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">Without Mess<br/>{currencySymbol}</th>
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">With Mess<br/>{currencySymbol}</th>
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">Without Mess<br/>{currencySymbol}</th>
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">With Mess<br/>{currencySymbol}</th>
              <th className="border border-red-800 p-1 font-semibold text-[8px] uppercase tracking-tighter">Without Mess<br/>{currencySymbol}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-800">
            {matrixData.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-gray-400 italic">No hostel rates found for this category.</td>
              </tr>
            ) : matrixData.map((row, rowIdx) => (
              <tr key={rowIdx} className={`transition-all duration-300 ${!isAnySelected ? 'hover:bg-gray-50 dark:hover:bg-dark-700/50' : ''}`}>
                <td className={`border border-gray-200 dark:border-dark-600 p-1.5 font-bold text-left whitespace-nowrap uppercase text-[10px] transition-all duration-300 ${
                  isAnySelected && selectedCell?.hostelName !== row.name 
                    ? 'opacity-20 grayscale text-gray-400 dark:text-dark-500' 
                    : 'text-gray-800 dark:text-dark-100'
                }`}>
                  {row.name}
                </td>
                
                {['Single', 'Double', 'Triple'].map((roomType) => {
                  return ['with', 'without'].map((messType) => {
                    const amount = row[roomType][messType];
                    const rateId = row[roomType].rateId;
                    const isSelected = selectedCell?.hostelName === row.name && selectedCell?.roomType === roomType && selectedCell?.messType === messType;
                    const isDisabled = amount === null || amount === undefined;
                    const displayAmt = isDisabled ? '-' : amount.toLocaleString();

                    return (
                      <td 
                        key={`${roomType}-${messType}`} 
                        className={`border border-gray-200 dark:border-dark-600 px-1 py-1.5 transition-all duration-300 ${
                          isSelected 
                            ? 'bg-primary-50 dark:bg-primary-900/40 ring-2 ring-inset ring-primary-500 relative z-10' 
                            : isAnySelected 
                              ? 'opacity-20 grayscale' 
                              : ''
                        } ${
                          isDisabled 
                            ? 'bg-gray-50 dark:bg-dark-900/50 cursor-not-allowed' 
                            : 'cursor-pointer hover:bg-primary-50/50 dark:hover:bg-primary-900/10'
                        }`}
                        onClick={() => handleCellSelect(row.name, roomType, messType, amount, rateId)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {!isDisabled && (
                            <input 
                              type="radio" 
                              checked={isSelected}
                              onChange={() => {}} // handled by cell click
                              className="w-3 h-3 text-primary-600 focus:ring-primary-500 cursor-pointer"
                            />
                          )}
                          <span className={`${isSelected ? 'font-bold text-primary-700 dark:text-primary-400' : 'font-medium text-gray-700 dark:text-dark-300'}`}>
                            {displayAmt}
                          </span>
                        </div>
                      </td>
                    );
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pt-2 flex flex-col md:flex-row justify-between items-end md:items-center border-t border-gray-100 dark:border-dark-700 mt-6 pt-6 gap-4">
        <div className="flex flex-col gap-1 w-full md:w-auto">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500 dark:text-dark-400">Base Academic Fee:</span>
            <span className="font-semibold text-gray-700 dark:text-dark-200">{currencySymbol} {academicAmount.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500 dark:text-dark-400">Selected Hostel Fee:</span>
            <span className="font-semibold text-gray-700 dark:text-dark-200">{currencySymbol} {hostelAmount.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 pt-1 border-t border-gray-200 dark:border-dark-700">
            <span className="text-sm font-bold text-gray-900 dark:text-dark-100">GRAND TOTAL:</span>
            <span className="text-xl font-black text-primary-600 dark:text-primary-400">
              {currencyLabel} {grandTotal.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex gap-3 w-full md:w-auto justify-end">
          <Button variant="outlined" onClick={onBack}>Back</Button>
          <Button color="primary" onClick={handleNext}>Next: Finalize Total</Button>
        </div>
      </div>
    </div>
  );
};
